import { TFunction } from 'i18next';
import { ControlsHubPanelsAssociatedToRisk } from '@interfaces/central-monitoring';
import { TimeFrequencyType } from 'types/graphql-global-types';
import { convertReviewFrequency } from '@utils/convertor';
import { mapRiskReviewStatus, mapRiskStatus } from '@utils/risk-helper';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';

export type FilterableRisk = CompleteRiskFields & {
    reviewFrequency: string;
    convertedOwner: { name: string };
    vendorList: string | undefined;
    countryList: string | undefined;
    riskRating: string;
    numLinkedCMControls: number;
    oot: string;
    status: string;
    reviewStatus: string;
    ltd: number;
    impact: number | undefined;
};

const evaluationDetails = (risk: CompleteRiskFields): { ltd: number; impact: number | undefined } => {
    const history = risk.evaluationHistory;
    if (!history?.length) return { ltd: 0, impact: undefined };

    const mostRecent = history.reduce((newest, item) =>
        new Date(item?.updated).getTime() > new Date(newest?.updated).getTime() ? item : newest,
    );
    return {
        ltd: (mostRecent?.likelihood ?? 0) * (mostRecent?.detectability ?? 0),
        impact: mostRecent?.impact,
    };
};

const getOOTValue = (
    t: TFunction,
    latestOOTValuesForPanel: ControlsHubPanelsAssociatedToRisk[] | undefined,
    associatedPanelRefs: string[] | null,
): string => {
    if (!associatedPanelRefs || associatedPanelRefs.length === 0) return t('shared.notApplicable');
    const panelData = latestOOTValuesForPanel?.filter((panel) => associatedPanelRefs.includes(panel.panelRef!));

    if (panelData?.filter((panel) => panel.active).length === 0) return t('shared.notApplicable');

    if (panelData?.some((panel) => panel?.status && (panel.status.includes('Red') || panel.status.includes('Amber'))))
        return t('shared.yes');

    return t('shared.no');
};

export const buildFilterableRisks = (
    risks: CompleteRiskFields[],
    t: TFunction,
    hasCMControlsPermission: boolean,
    latestOOTValuesForPanel: ControlsHubPanelsAssociatedToRisk[] | undefined,
): FilterableRisk[] =>
    risks.map((risk) => ({
        ...risk,
        ...evaluationDetails(risk),
        status: risk.status ? mapRiskStatus(risk.status) : '',
        reviewFrequency: convertReviewFrequency(
            risk.reviewFrequencyPeriod ?? 0,
            TimeFrequencyType[risk.reviewFrequencyUnits ?? 'NONE'],
        ),
        convertedOwner: { name: risk.owner ? `${risk.owner.firstName} ${risk.owner.lastName}` : '' },
        vendorList: risk.vendors?.map((v) => v?.name).join(', '),
        countryList: risk.countries?.map((c) => c?.name).join(', '),
        riskRating: t(`riskRating.${risk.score?.colour}`),
        reviewStatus: risk.reviewStatus ? mapRiskReviewStatus(risk.reviewStatus) : '',
        numLinkedCMControls: risk.associatedControlsHubPanelIds?.length ?? 0,
        oot: hasCMControlsPermission
            ? getOOTValue(t, latestOOTValuesForPanel, risk.associatedControlsHubPanelIds as string[])
            : '',
    }));
